import { useEffect, useRef, useState } from 'react';
import { Button, Input, message, Spin } from 'antd';
import styles from './DisclaimerAdmin.module.scss';
import {
  publishDisclaimer,
  getCurrentDisclaimer,
  type DisclaimerDto,
  type CurrentDisclaimerDto,
} from '../../apis/DisclaimerService';

const { TextArea } = Input;

const DisclaimerAdmin = () => {
  const [title, setTitle] = useState('Rubix Disclaimer');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [published, setPublished] = useState<DisclaimerDto | null>(null);

  const [current, setCurrent] = useState<CurrentDisclaimerDto | null>(null);
  const [currentLoading, setCurrentLoading] = useState(true);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const currentControllerRef = useRef<AbortController | null>(null);

  const titleError = title.length === 0 || title.length > 200;
  const contentError = content.trim().length === 0;
  const isValid = !titleError && !contentError;

  useEffect(() => {
    const controller = new AbortController();
    currentControllerRef.current = controller;
    getCurrentDisclaimer(controller.signal)
      .then((data) => setCurrent(data))
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 404) {
          setCurrentError('No Disclaimer has been published yet.');
        } else {
          setCurrentError('Failed to load the currently published Disclaimer.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setCurrentLoading(false);
      });

    return () => controller.abort();
  }, []);

  const handlePublish = async () => {
    if (!isValid || currentLoading) return;

    setSubmitting(true);
    try {
      currentControllerRef.current?.abort();
      const result = await publishDisclaimer({ title, content });
      setPublished(result);
      setCurrent(result);
      setCurrentError(null);
      setCurrentLoading(false);
      message.success(`Disclaimer v${result.version} published. All users will be re-prompted.`);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        message.error('You do not have admin permissions to publish a Disclaimer.');
      } else if (err?.response?.status === 422) {
        message.error('Validation failed. Check title (1–200 chars) and content (non-empty).');
      } else {
        message.error('Failed to publish Disclaimer. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container} data-testid="disclaimer-admin">
      <h2 className={styles.heading}>Publish Disclaimer</h2>
      <p className={styles.description}>
        Publishing a new version will deactivate the previous disclaimer and require all users to re-acknowledge.
      </p>

      <div className={styles.layout}>
        <div className={styles.draftColumn}>
          <h3 className={styles.columnHeading}>New Version (Draft)</h3>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="disclaimer-title">Title</label>
            <Input
              id="disclaimer-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              status={title.length > 0 && titleError ? 'error' : undefined}
              placeholder="Disclaimer title (max 200 chars)"
              data-testid="disclaimer-admin-title"
            />
            <span className={styles.charCount}>{title.length}/200</span>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="disclaimer-content">Content</label>
            <TextArea
              id="disclaimer-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={16}
              placeholder="Full text of the disclaimer. Line breaks will be preserved."
              status={content.length > 0 && contentError ? 'error' : undefined}
              data-testid="disclaimer-admin-content"
            />
            <span className={styles.hint}>Line breaks are preserved as-is. No HTML required.</span>
          </div>

          <div className={styles.actions}>
            <Button
              type="primary"
              onClick={handlePublish}
              loading={submitting}
              disabled={!isValid || submitting || currentLoading}
              data-testid="disclaimer-admin-publish-btn"
            >
              Publish New Version
            </Button>
          </div>

          {published && (
            <div className={styles.result} data-testid="disclaimer-admin-result">
              <p><strong>Published successfully</strong></p>
              <ul>
                <li>Version: {published.version}</li>
                <li>ID: {published.id}</li>
                <li>Effective: {new Date(published.effectiveAt).toLocaleString()}</li>
              </ul>
            </div>
          )}
        </div>

        <div className={styles.publishedColumn} data-testid="disclaimer-admin-current">
          <h3 className={styles.columnHeading}>Currently Published</h3>

          {currentLoading && (
            <div className={`${styles.publishedPanel} ${styles.publishedPanelCentered}`}>
              <Spin data-testid="disclaimer-admin-current-loading" />
            </div>
          )}

          {!currentLoading && currentError && (
            <div className={`${styles.publishedPanel} ${styles.publishedPanelCentered}`}>
              <p className={styles.error} data-testid="disclaimer-admin-current-error">{currentError}</p>
            </div>
          )}

          {!currentLoading && !currentError && current && (
            <div className={styles.publishedPanel}>
              <span className={styles.label}>Title</span>
              <div className={styles.publishedTitle} data-testid="disclaimer-admin-current-title">
                {current.title}
              </div>
              <span className={styles.label}>Content</span>
              <div className={styles.publishedContent} data-testid="disclaimer-admin-current-content">
                {current.content}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DisclaimerAdmin;
